import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  FaArrowRight, FaArrowLeft, FaCheckCircle,
  FaClock, FaMoneyBillWave, FaGraduationCap,
  FaClipboardList
} from 'react-icons/fa';
import programsData from '../programsData';
import { TimeContext } from '../TimeContext';
import { trackProgramView } from '../analytics/events';
import './Homepage.css';
import { SiteHeader, SiteFooter } from './Homepage';

/* ─── Department helper (mirrors Programs.jsx) ─── */
const getDept = (id) => {
  if (id <= 5)  return { label: 'Travel & Tourism',   color: '#4169E1' };
  if (id <= 14) return { label: 'Hospitality',         color: '#D97706' };
  if (id <= 30) return { label: 'Business',            color: '#1E3A8A' };
  if (id <= 41) return { label: 'Languages',           color: '#5a9200' };
  if (id <= 47) return { label: "Int'l Entry Exams",   color: '#7C3AED' };
  return         { label: 'Computer & IT',             color: '#0891B2' };
};

const ProgramDetails = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const currentTime = useContext(TimeContext);
  const [program, setProgram] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    const found = programsData.find(p => p.id === parseInt(id));
    setProgram(found);
    if (found) {
      /* Builds a retargeting audience of people who read a specific
         course page - far higher intent than a general site visitor. */
      trackProgramView(found);

      // 3 related programs from same approximate dept range
      const others = programsData
        .filter(p => p.id !== found.id && Math.abs(p.id - found.id) < 20)
        .slice(0, 3);
      setRelated(others.length >= 3 ? others : programsData.filter(p => p.id !== found.id).slice(0, 3));
    }
    window.scrollTo(0, 0);
  }, [id]);

  const navLinks = [
    { to: '/',            label: t('nav.home',       'Home'),        ariaLabel: t('nav.home',       'Home')        },
    { to: '/programs',    label: t('nav.programs',   'Programs'),    ariaLabel: t('nav.programs',   'Programs')    },
    { to: '/campus-life', label: t('nav.campusLife', 'Campus Life'), ariaLabel: t('nav.campusLife', 'Campus Life') },
    { to: '/alumni',      label: t('nav.alumni',     'Alumni'),      ariaLabel: t('nav.alumni',     'Alumni')      },
    { to: '/enrollment',  label: t('nav.enrollment', 'Enrollment'),  ariaLabel: t('nav.enrollment', 'Enrollment')  },
  ];

  /* ─── Not found state ─── */
  if (!program) {
    return (
      <div style={{ minHeight: '100vh', background: '#EEF1FC', fontFamily: "'Poppins',sans-serif" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Fraunces:ital,wght@0,700;0,900;1,700;1,900&display=swap');`}</style>
        <SiteHeader t={t} i18n={i18n} navLinks={navLinks} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', textAlign: 'center', padding: 24 }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>🔍</div>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 28, fontWeight: 900, color: '#1E3A8A', marginBottom: 12 }}>
            Program Not Found
          </h2>
          <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 28 }}>
            The program you're looking for doesn't exist or may have moved.
          </p>
          <Link to="/programs" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }}>
            <FaArrowLeft style={{ fontSize: 11 }} /> Browse All Programs
          </Link>
        </div>
        <SiteFooter t={t} currentTime={currentTime} />
      </div>
    );
  }

  const dept = getDept(program.id);

  const feeItems = [
    program.feePerLevel && { icon: <FaMoneyBillWave />, label: 'Fee per Level',   value: `Ksh ${program.feePerLevel?.toLocaleString()}` },
    program.feePerTerm  && { icon: <FaMoneyBillWave />, label: 'Fee per Term',    value: `Ksh ${program.feePerTerm?.toLocaleString()}`  },
    program.admissionFee && { icon: <FaGraduationCap />, label: 'Admission Fee',  value: `Ksh ${program.admissionFee?.toLocaleString()} (non-refundable)` },
    program.examinationFee && { icon: <FaClipboardList />, label: 'Examination Fee', value: `Ksh ${program.examinationFee?.toLocaleString()}` },
    { icon: <FaClock />, label: 'Duration', value: program.duration },
  ].filter(Boolean);

  return (
    <div style={{ minHeight: '100vh', background: '#EEF1FC', fontFamily: "'Poppins',sans-serif" }}>
      <Helmet>
        <title>
          {t('programDetails.title', { name: program.name, defaultValue: `${program.name} — San Tomas Tourism College` })}
        </title>
        <meta name="description" content={t('programDetails.description', { name: program.name, defaultValue: `Details of the ${program.name} program at San Tomas Tourism College, Nairobi.` })} />
        <meta name="keywords" content={t('programDetails.keywords', { name: program.name, defaultValue: `${program.name}, San Tomas Tourism College, Nairobi, tourism education` })} />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Fraunces:ital,wght@0,700;0,900;1,700;1,900&display=swap" rel="stylesheet" />
        <link rel="canonical" href={`https://santomastourismcollege.com/programs/${id}`} />
        <meta property="og:title" content={`${program.name} — San Tomas Tourism College`} />
        <meta property="og:description" content={program.description} />
        <meta property="og:image" content="/social-banner.jpg" />
        <meta property="og:url" content={`https://santomastourismcollege.com/programs/${id}`} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <a href="#main-content" className="sr-only focus:not-sr-only">
        {t('a11y.skipToContent', 'Skip to main content')}
      </a>

      <SiteHeader t={t} i18n={i18n} navLinks={navLinks} />

      {/* ── HERO ── */}
      <section className="sttc-hero" style={{ minHeight: '42vh' }}>
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-geo" aria-hidden="true" style={{ background: `linear-gradient(135deg, ${dept.color}22, transparent)` }} />
        <div className="hero-circle-1" aria-hidden="true" />

        <div className="sttc-container" style={{ position: 'relative', zIndex: 2, paddingTop: 128, paddingBottom: 56 }}>
          {/* Back link */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            <Link to="/programs"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.65)', textDecoration: 'none', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 24, transition: 'color 0.3s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#FBBF24'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}
            >
              <FaArrowLeft style={{ fontSize: 10 }} />
              {t('nav.programs', 'All Programs')}
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
            {/* Dept badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: `${dept.color}28`, border: `1px solid ${dept.color}55`, borderRadius: 50, padding: '5px 14px', marginBottom: 20 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: dept.color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                {dept.label}
              </span>
            </div>

            <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: 'clamp(34px,5vw,58px)', fontWeight: 900, color: '#fff', lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 16, maxWidth: 680 }}>
              {program.name}
            </h1>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', maxWidth: 560, lineHeight: 1.8, fontWeight: 300 }}>
              {program.description}
            </p>

            {/* Quick info pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 50, padding: '7px 16px', fontSize: 12, color: 'rgba(255,255,255,0.88)', fontWeight: 500 }}>
                <FaClock style={{ color: '#FBBF24', fontSize: 11 }} />
                {program.duration}
              </div>
              {(program.feePerLevel || program.feePerTerm) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.35)', borderRadius: 50, padding: '7px 16px', fontSize: 12, color: '#FBBF24', fontWeight: 600 }}>
                  <FaMoneyBillWave style={{ fontSize: 11 }} />
                  Ksh {(program.feePerLevel || program.feePerTerm)?.toLocaleString()}
                  {program.feePerLevel ? ' / Level' : ' / Term'}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="sttc-stripe" />

      {/* ── MAIN CONTENT ── */}
      <section id="main-content" style={{ padding: '72px 0 96px' }}>
        <div className="sttc-container">
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 40, alignItems: 'start' }}>

            {/* ─ LEFT: Learning outcomes ─ */}
            <div>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <div className="section-eyebrow">Curriculum</div>
                <h2 className="section-title" style={{ marginBottom: 8 }}>
                  {t('programDetails.learningOutcomes', 'What You Will ')}
                  <em>Learn</em>
                </h2>
                <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 36, lineHeight: 1.75 }}>
                  This program equips you with the skills, knowledge, and confidence to excel in the field.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                  {(program.learningOutcomes || []).map((outcome, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.05 }}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 18px', background: '#fff', border: '1px solid rgba(65,105,225,0.1)', borderRadius: 10, boxShadow: '0 2px 8px rgba(65,105,225,0.04)', transition: 'all 0.3s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(65,105,225,0.3)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(65,105,225,0.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(65,105,225,0.1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(65,105,225,0.04)'; }}
                    >
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: `${dept.color}18`, border: `1px solid ${dept.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                        <FaCheckCircle style={{ color: dept.color, fontSize: 11 }} />
                      </div>
                      <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.65 }}>{outcome}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* ─ Enrol CTA band ─ */}
              <motion.div
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}
                style={{ marginTop: 48, background: `linear-gradient(135deg, #1E3A8A, #4169E1)`, borderRadius: 16, padding: '36px 36px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}
              >
                <div>
                  <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 900, color: '#fff', marginBottom: 6 }}>
                    Ready to Enrol?
                  </h3>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.72)', lineHeight: 1.6, maxWidth: 380 }}>
                    Applications are now open. Join a community of passionate learners at San Tomas.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Link to="/enrollment" className="btn-primary" style={{ textDecoration: 'none' }}
                    aria-label={t('programDetails.applyAria', 'Apply to this program')}>
                    <span>{t('programDetails.apply', 'Apply Now')}</span>
                    <FaArrowRight style={{ fontSize: 11 }} />
                  </Link>
                  <Link to="/programs" className="btn-secondary" style={{ textDecoration: 'none' }}>
                    All Programs
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* ─ RIGHT: Sticky sidebar ─ */}
            <div style={{ position: 'sticky', top: 96 }}>
              {/* Fee & info card */}
              <motion.div
                initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
                style={{ background: '#fff', borderRadius: 16, border: `2px solid ${dept.color}22`, overflow: 'hidden', boxShadow: '0 8px 32px rgba(65,105,225,0.08)' }}
              >
                {/* Card header */}
                <div style={{ background: `linear-gradient(135deg, ${dept.color}, #1E3A8A)`, padding: '24px 28px' }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>
                    Program Overview
                  </div>
                  <div style={{ fontFamily: "'Fraunces',serif", fontSize: 18, fontWeight: 900, color: '#fff', lineHeight: 1.3 }}>
                    {program.name}
                  </div>
                  <div style={{ display: 'inline-block', marginTop: 10, padding: '3px 12px', borderRadius: 50, background: 'rgba(255,255,255,0.15)', fontSize: 11, color: '#fff', fontWeight: 600, letterSpacing: '0.06em' }}>
                    {dept.label}
                  </div>
                </div>

                {/* Fee rows */}
                <div style={{ padding: '8px 0' }}>
                  {feeItems.map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 14, padding: '14px 24px', borderBottom: i < feeItems.length - 1 ? '1px solid rgba(65,105,225,0.06)' : 'none', alignItems: 'flex-start' }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${dept.color}12`, border: `1px solid ${dept.color}28`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: dept.color, fontSize: 13, flexShrink: 0 }}>
                        {item.icon}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3, fontWeight: 600 }}>{item.label}</div>
                        <div style={{ fontSize: 14, color: '#1E3A8A', fontWeight: 700 }}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ padding: '16px 24px 24px' }}>
                  <Link to="/enrollment" className="btn-primary"
                    style={{ width: '100%', justifyContent: 'center', textDecoration: 'none', display: 'flex', fontSize: 13, border: 'none' }}
                    aria-label="Apply to this program"
                  >
                    <span>Apply for This Program</span>
                    <FaArrowRight style={{ fontSize: 11 }} />
                  </Link>
                </div>
              </motion.div>

              {/* Requirements card */}
              <motion.div
                initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
                style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(65,105,225,0.1)', padding: '24px 28px', marginTop: 16, boxShadow: '0 4px 16px rgba(65,105,225,0.04)' }}
              >
                <div style={{ fontSize: 11, color: '#4169E1', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 16 }}>
                  Admission Requirements
                </div>
                {[
                  '2 recent passport photos (colored)',
                  'Copy of school leaving certificate',
                  'Copy of result slip / academic transcript',
                  `Admission fee: Ksh ${(program.admissionFee || 2500).toLocaleString()} (non-refundable)`,
                ].map((req, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-start' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(65,105,225,0.08)', border: '1px solid rgba(65,105,225,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                      <span style={{ fontSize: 8, color: '#4169E1', fontWeight: 800 }}>✓</span>
                    </div>
                    <span style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.65 }}>{req}</span>
                  </div>
                ))}
              </motion.div>

              {/* Need help? */}
              <div style={{ marginTop: 14, padding: '16px 20px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 12, textAlign: 'center' }}>
                <p style={{ fontSize: 12, color: '#92400E', fontWeight: 600, marginBottom: 4 }}>Need help deciding?</p>
                <a href="mailto:info@santomastourismcollege.com" style={{ fontSize: 12, color: '#D97706', fontWeight: 600, textDecoration: 'none' }}>
                  info@santomastourismcollege.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── RELATED PROGRAMS ── */}
      {related.length > 0 && (
        <>
          <div className="sttc-stripe" />
          <section style={{ padding: '72px 0', background: '#fff' }}>
            <div className="sttc-container">
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 40, gap: 16 }}>
                <div>
                  <div className="section-eyebrow">Keep Exploring</div>
                  <h2 className="section-title">Related <em>Programs</em></h2>
                </div>
                <Link to="/programs" className="btn-outline" style={{ textDecoration: 'none', fontSize: 12 }}>
                  View All <FaArrowRight style={{ fontSize: 10 }} />
                </Link>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
                {related.map((prog, i) => {
                  const relDept = getDept(prog.id);
                  return (
                    <motion.div
                      key={prog.id}
                      initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.07 }}
                      className="program-card"
                    >
                      <div className="program-card-header" style={{ background: `linear-gradient(135deg, ${relDept.color}, #1E3A8A)` }}>
                        <div className="program-card-icon">
                          <img src={`https://picsum.photos/32/32?random=${prog.id}`} alt="" loading="lazy" decoding="async" onError={e => e.target.parentNode.classList.add('hidden')} />
                        </div>
                        <h3>{prog.name}</h3>
                      </div>
                      <div className="program-card-body">
                        <p>{(prog.description || '').slice(0, 90)}{(prog.description || '').length > 90 ? '…' : ''}</p>
                        <div className="program-duration">{prog.duration}</div>
                        <Link to={`/programs/${prog.id}`} className="program-card-link">
                          Learn more <FaArrowRight />
                        </Link>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        </>
      )}

      <SiteFooter t={t} currentTime={currentTime} />
    </div>
  );
};

export default ProgramDetails;